"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";

import { useAssessmentRhythm } from "./use-assessment-rhythm";

export type AssessmentEntryKey =
  | "vocabulary"
  | "chat"
  | "courses"
  | "practice"
  | "profile"
  | "settings";

export interface UseAssessmentRhythmBridgeOptions {
  userId?: string | null;
  locale: string;
  entry: AssessmentEntryKey;
  enabled?: boolean;
  remindCooldownMs?: number;
  conversationCount?: number;
}

interface BridgeStorage {
  lastRequestKey?: string | null;
  hiddenUntil?: number | null;
}

export interface BannerState {
  visible: boolean;
  variant: "info" | "success" | "warning";
  title: string;
  description: string;
  nextHintLabel?: string;
  statusLabel?: string;
  updatedAtLabel?: string;
  requestId?: string | null;
  actionLabel: string;
  actionHref?: string;
  remindLabel: string;
}

const ENTRY_CONFIG: Record<
  AssessmentEntryKey,
  {
    buildHref: (locale: string) => string;
    actionKey: string;
    actionFallback: string;
  }
> = {
  vocabulary: {
    buildHref: (locale: string) => `/${locale}/chat`,
    actionKey: "action.vocabulary",
    actionFallback: "Resume chat assessment",
  },
  chat: {
    buildHref: (locale: string) => `/${locale}/practice`,
    actionKey: "action.chat",
    actionFallback: "Open practice hub",
  },
  courses: {
    buildHref: (locale: string) => `/${locale}/practice`,
    actionKey: "action.courses",
    actionFallback: "Start practice session",
  },
  practice: {
    buildHref: (locale: string) => `/${locale}/practice`,
    actionKey: "action.practice",
    actionFallback: "Resume practice",
  },
  profile: {
    buildHref: (locale: string) => `/${locale}/profile`,
    actionKey: "action.profile",
    actionFallback: "Open learning profile",
  },
  settings: {
    buildHref: (locale: string) => `/${locale}/settings`,
    actionKey: "action.settings",
    actionFallback: "Open settings",
  },
};

const STORAGE_PREFIX = "tm-assessment-banner";
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;
const DEFAULT_REMIND_COOLDOWN_MS = 60 * 60 * 1000;
const HINT_FALLBACKS = {
  ready: "Ready next conversation",
  cooldown: "Cooling down, we will remind you",
  waiting_turns: "Keep chatting to trigger the assessment",
  disabled: "Waiting for first assessment",
} as const;

const STATUS_FALLBACKS = {
  created: "Assessment updated",
  failed: "Assessment failed",
  skipped: "Conditions not met",
} as const;

const BANNER_FALLBACKS = {
  defaultTitle: "Assessment reminder",
  defaultDescription: "Stay on rhythm to keep your assessment fresh.",
  readyTitle: "Ready for next assessment",
  readyDescription:
    "Cooldown finished. Start a session to trigger the next assessment.",
  updateTitle: "Assessment update",
  failedDescription: "Assessment failed. Please try again later.",
  successDescription: "Your latest proficiency assessment is ready.",
  remindLabel: "Remind me later",
};

function loadStorage(key: string): BridgeStorage {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return {};
    return {
      lastRequestKey:
        typeof parsed.lastRequestKey === "string" ||
        parsed.lastRequestKey === null
          ? parsed.lastRequestKey
          : undefined,
      hiddenUntil:
        typeof parsed.hiddenUntil === "number" ? parsed.hiddenUntil : undefined,
    };
  } catch {
    return {};
  }
}

function persistStorage(key: string, value: BridgeStorage) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore persistence errors
  }
}

export interface AssessmentRhythmBridgeResult {
  assessment: ReturnType<typeof useAssessmentRhythm>;
  bannerState: BannerState;
  remindLater: () => void;
  dismissForDay: () => void;
  acknowledge: () => void;
}

export function useAssessmentRhythmBridge({
  userId,
  locale,
  entry,
  enabled = true,
  remindCooldownMs = DEFAULT_REMIND_COOLDOWN_MS,
  conversationCount,
}: UseAssessmentRhythmBridgeOptions): AssessmentRhythmBridgeResult {
  const tBridge = useTranslations("chat.assessmentBridge");
  const assessment = useAssessmentRhythm({
    userId,
    enabled: enabled && Boolean(userId),
    conversationCount,
  });
  const storageKey = `${STORAGE_PREFIX}:${entry}`;
  const [storage, setStorage] = useState<BridgeStorage>(() =>
    loadStorage(storageKey),
  );

  useEffect(() => {
    setStorage(loadStorage(storageKey));
  }, [storageKey]);

  useEffect(() => {
    persistStorage(storageKey, storage);
  }, [storage, storageKey]);

  const entryConfig = ENTRY_CONFIG[entry];
  const translate = useCallback(
    (key: string, fallback: string, values?: Record<string, unknown>) => {
      try {
        return tBridge(key as any, values);
      } catch {
        if (values) {
          return Object.entries(values).reduce(
            (acc, [k, v]) =>
              acc.replace(new RegExp(`\\{${k}\\}`, "g"), String(v)),
            fallback,
          );
        }
        return fallback;
      }
    },
    [tBridge],
  );

  const requestKey =
    assessment.latest?.requestId ?? assessment.latest?.updatedAt ?? null;
  const hidden = Boolean(
    storage.hiddenUntil && storage.hiddenUntil > Date.now(),
  );
  const unseenRequest = Boolean(
    requestKey && storage.lastRequestKey !== requestKey,
  );
  const nextHintStatus = assessment.nextAssessmentHint?.status ?? "disabled";

  const shouldDisplay =
    !hidden && (unseenRequest || nextHintStatus === "ready");

  const bannerState: BannerState = useMemo(() => {
    let variant: BannerState["variant"] = "info";
    let title = translate(
      "banner.default_title",
      BANNER_FALLBACKS.defaultTitle,
    );
    let description = translate(
      "banner.default_description",
      BANNER_FALLBACKS.defaultDescription,
    );

    if (unseenRequest && assessment.latest) {
      const status = assessment.latest.status ?? "created";
      variant =
        status === "failed"
          ? "warning"
          : status === "created"
            ? "success"
            : "info";
      title =
        translate(
          `status.${status}`,
          STATUS_FALLBACKS[status as keyof typeof STATUS_FALLBACKS] ??
            BANNER_FALLBACKS.updateTitle,
        ) ?? translate("banner.update_title", BANNER_FALLBACKS.updateTitle);
      description =
        status === "failed"
          ? (assessment.latest.reason ??
            translate(
              "banner.failed_description",
              BANNER_FALLBACKS.failedDescription,
            ))
          : translate(
              "banner.success_description",
              BANNER_FALLBACKS.successDescription,
            );
    } else if (shouldDisplay && nextHintStatus === "ready") {
      variant = "info";
      title = translate("banner.ready_title", BANNER_FALLBACKS.readyTitle);
      description = translate(
        "banner.ready_description",
        BANNER_FALLBACKS.readyDescription,
      );
    }

    const nextHintLabel =
      shouldDisplay && assessment.nextAssessmentHint
        ? translate(
            `hint.${assessment.nextAssessmentHint.status}`,
            HINT_FALLBACKS[
              assessment.nextAssessmentHint
                .status as keyof typeof HINT_FALLBACKS
            ],
          )
        : undefined;

    const statusLabel = assessment.latest?.status
      ? translate(
          `status.${assessment.latest.status}`,
          STATUS_FALLBACKS[
            assessment.latest.status as keyof typeof STATUS_FALLBACKS
          ],
        )
      : undefined;

    const updatedAtLabel = assessment.latest?.updatedAt
      ? new Date(assessment.latest.updatedAt).toLocaleString()
      : undefined;

    return {
      visible: shouldDisplay,
      variant,
      title,
      description,
      nextHintLabel,
      statusLabel,
      updatedAtLabel,
      requestId: requestKey,
      actionLabel: translate(entryConfig.actionKey, entryConfig.actionFallback),
      actionHref: entryConfig.buildHref(locale),
      remindLabel: translate("remind_label", BANNER_FALLBACKS.remindLabel),
    };
  }, [
    assessment.latest,
    assessment.nextAssessmentHint,
    entryConfig,
    translate,
    locale,
    nextHintStatus,
    requestKey,
    shouldDisplay,
    unseenRequest,
  ]);

  const acknowledge = useCallback(() => {
    if (!requestKey) return;
    setStorage((prev) => {
      if (prev.lastRequestKey === requestKey) return prev;
      return { ...prev, lastRequestKey: requestKey };
    });
  }, [requestKey]);

  const remindLater = useCallback(() => {
    const snoozeUntil = Date.now() + remindCooldownMs;
    setStorage((prev) => ({
      ...prev,
      hiddenUntil: snoozeUntil,
      lastRequestKey: requestKey ?? prev.lastRequestKey ?? null,
    }));
  }, [remindCooldownMs, requestKey]);

  const dismissForDay = useCallback(() => {
    const hideUntil = Date.now() + DISMISS_DURATION_MS;
    setStorage((prev) => ({
      ...prev,
      hiddenUntil: hideUntil,
      lastRequestKey: requestKey ?? prev.lastRequestKey ?? null,
    }));
  }, [requestKey]);

  return {
    assessment,
    bannerState: bannerState.visible
      ? bannerState
      : {
          ...bannerState,
          visible: false,
        },
    remindLater,
    dismissForDay,
    acknowledge,
  };
}
